'use client';

import { createContext, useContext, useEffect, useMemo, type PropsWithChildren } from 'react';
import { APP_COPY, type AppCopy } from '@/i18n/catalogs';
import { showDocumentLanguage } from '@/i18n/document-lang';
import { localeLanguage } from '@/i18n/eu-locales';

export type AppCatalogLocale = keyof typeof APP_COPY;

export function resolveCatalogLocale(locale: string | null | undefined): AppCatalogLocale {
  const language = localeLanguage(locale);
  return Object.hasOwn(APP_COPY, language) ? (language as AppCatalogLocale) : 'en';
}

export function interpolateCopy(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{([^}]+)\}/g, (match, key: string) =>
    Object.hasOwn(values, key) ? String(values[key]) : match,
  );
}

type AppI18nValue = {
  requestedLocale: string;
  locale: string;
  catalogLocale: AppCatalogLocale;
  copy: AppCopy;
};

const AppI18nContext = createContext<AppI18nValue>({
  requestedLocale: 'en',
  locale: 'en',
  catalogLocale: 'en',
  copy: APP_COPY.en,
});

export function AppI18nProvider({
  locale,
  children,
}: PropsWithChildren<{ locale: string | null | undefined }>) {
  const requestedLocale = locale || 'en';
  const catalogLocale = resolveCatalogLocale(requestedLocale);
  const value = useMemo(
    () => ({
      requestedLocale,
      locale: catalogLocale,
      catalogLocale,
      copy: APP_COPY[catalogLocale],
    }),
    [requestedLocale, catalogLocale],
  );
  // Also remembered on this device, so the next first paint (ui/boot.ts) has it.
  useEffect(() => {
    showDocumentLanguage(catalogLocale);
  }, [catalogLocale]);
  return <AppI18nContext.Provider value={value}>{children}</AppI18nContext.Provider>;
}

export const useAppCopy = (): AppCopy => useContext(AppI18nContext).copy;
export const useAppLocale = (): string => useContext(AppI18nContext).locale;
export const useRequestedAppLocale = (): string => useContext(AppI18nContext).requestedLocale;
export const useAppCatalogLocale = (): AppCatalogLocale => useContext(AppI18nContext).catalogLocale;
